import React, { useState } from "react";
import ConnectionStatus from "../../../utils/ConnectionStatus";
import { useSelector } from "react-redux";
import ReportForm from "./ReportForm";
import SubmitReport from "./SubmitReport";
import {
  BottomNavigation,
  BottomNavigationAction,
  Button,
  Modal,
  Box,
  Typography,
  Link,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import {
  Stack,
  Tooltip,
  useMediaQuery,
  useTheme,
  Paper,
  Divider,
} from "@mui/material";
import dayjs from "dayjs";

import "./css/ReportEditor.css";

function ReportEditor({
  report,
  setReport,
  restaurant,
  setRestaurant,
  setTypeActivity,
  typeActivity,
  DateEvenement, // 🔹 Nouveau : date envoyée depuis le parent
}) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [openModalSubmit, setOpenModalSubmit] = useState(false);
  const [creatingReport, setCreatingReport] = useState(false); // 🔹 empêche double clic

  const { t } = useTranslation(["Report", "Common"]);
  const userState = useSelector((state) => state.user);

  //ui botton
  const theme = useTheme();
  const coraBlue = "#0057B8";
  const coraBlueLight = "#1E88E5";
  const coraOrange = "#F28C00";

  //jout d’un état pour le modal de confirmation
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const handleOpenDeleteConfirm = () => setOpenDeleteConfirm(true);
  const handleCloseDeleteConfirm = () => setOpenDeleteConfirm(false);

  // 🔹 States pour MUI Snackbar / Alert
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // "success" | "error" | "info" | "warning"

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const styleModal = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
  };

  const handleStatusChange = (status) => setIsOnline(status);

  // 🔹 Création d'un nouveau report
  const handleNewReport = async () => {
    if (creatingReport) return; // empêche double clic

    if (!typeActivity || !restaurant || !DateEvenement) {
      alert("Please select Type, Location and Date before creating a report.");
      return;
    }

    setCreatingReport(true);

    const newActivity = {
      id: 0,
      idTypeActivite: typeActivity.id,
      dateComplete: null,
      dateEvenement: dayjs(DateEvenement).format("YYYY-MM-DDTHH:mm:ss"),
      location: restaurant,
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Report/NewActivity`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
          body: JSON.stringify(newActivity),
        },
      );

      const data = await response.json();
      console.log("New report created:", data);
      setReport({ ...data }); // update state
    } catch (error) {
      console.error("Error creating new activity:", error);
      alert("Failed to create report. Please try again.");
    } finally {
      setCreatingReport(false);
    }
  };

  // 🔹 Suppression du report existant
  const handleDeleteReport = async () => {
    if (!report) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Report/DeleteReport/${report.idActivite}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
        },
      );

      if (response.ok) {
        setReport(null);
        setRestaurant(null);
        setTypeActivity(null);
        setSnackbarMessage("Report deleted successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } else {
        const errorText = await response.text();
        setSnackbarMessage(`Error deleting report: ${errorText}`);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error deleting report:", error);
      setSnackbarMessage(
        "An error occurred while deleting the report: " + error.message,
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleOpenModalSubmit = () => setOpenModalSubmit(true);
  const handleCloseModalSubmit = () => {
    setOpenModalSubmit(false);
    setReport(null);
    setRestaurant(null);
    setTypeActivity(null);
  };

  return (
    <div className="FormRepport__Question">
      <ConnectionStatus onStatusChange={handleStatusChange} />

      {/* 🔹 Bouton NewReport ergonomique */}
      <Paper
        elevation={4}
        sx={{
          width: "100%",
          mb: 2,
          borderRadius: 2,
          background: `linear-gradient(90deg, ${coraBlue} 0%, ${coraBlueLight} 70%)`,
        }}
      >
        <BottomNavigation
          showLabels
          sx={{
            backgroundColor: "transparent",

            "& .MuiBottomNavigationAction-root": {
              color: "rgba(255,255,255,0.9)",
              transition: "all 0.25s ease",
              minWidth: 0,

              "& .MuiSvgIcon-root": {
                fontSize: 26,
                transition: "transform 0.25s ease",
              },

              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.12)",
              },
            },

            "& .Mui-selected": {
              color: coraOrange,
              "& .MuiSvgIcon-root": {
                transform: "scale(1.2)",
              },
            },
          }}
        >
          {/* New Report */}
          <BottomNavigationAction
            label={creatingReport ? t("Creating...") : t("NewReport")}
            icon={<AddIcon />}
            onClick={handleNewReport}
            disabled={creatingReport || report != null}
            sx={{
              "&.Mui-disabled": {
                color: "rgba(255,255,255,0.4)",
              },
            }}
          />

          {/* Delete */}
          <BottomNavigationAction
            label={t("Delete")}
            icon={<DeleteIcon />}
            onClick={handleOpenDeleteConfirm}
            disabled={!report}
            sx={{
              "&:hover": {
                backgroundColor: "rgba(255,0,0,0.25)",
              },
              "&.Mui-disabled": {
                color: "rgba(255,255,255,0.4)",
              },
            }}
          />

          {/* Close */}
          <BottomNavigationAction
            label={t("CloseReport")}
            icon={<CloseIcon />}
            component={Link}
            href="/Reports"
          />
        </BottomNavigation>
      </Paper>

      {isOnline ? (
        <>
          {report &&
            report?.listeQuestions?.map((question) => (
              <ReportForm
                key={question.id}
                question={question}
                idActivite={report.idActivite}
                answers={report.listeReponses}
              />
            ))}

          {report && report.idActivite && (
            <Button
              variant="contained"
              color="success"
              onClick={handleOpenModalSubmit}
              sx={{ mt: 2 }}
            >
              {t("Submit")}
            </Button>
          )}
        </>
      ) : (
        <Typography sx={{ mt: 2, color: "red" }}>
          {t("You are offline!")}
        </Typography>
      )}

      {/* 🔹 Modal de submission */}
      {report && (
        <Modal
          open={openModalSubmit}
          onClose={handleCloseModalSubmit}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={styleModal}>
            <Typography id="modal-modal-title" variant="h6" component="h2">
              {t("Submission")}
            </Typography>
            <Typography id="modal-modal-description" sx={{ mt: 2 }}>
              <SubmitReport
                location={restaurant}
                idActivite={report.idActivite}
                close={handleCloseModalSubmit}
              />
            </Typography>
          </Box>
        </Modal>
      )}

      <Modal
        open={openDeleteConfirm}
        onClose={handleCloseDeleteConfirm}
        aria-labelledby="confirm-delete-title"
        aria-describedby="confirm-delete-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 350,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            textAlign: "center",
          }}
        >
          <Typography id="confirm-delete-title" variant="h6" component="h2">
            {t("ConfirmDelete")}
          </Typography>
          <Typography id="confirm-delete-description" sx={{ mt: 2, mb: 3 }}>
            {t("MessageDeleteThisReport")}
          </Typography>

          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleCloseDeleteConfirm}
            >
              {t("Cancel")}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={async () => {
                handleCloseDeleteConfirm(); // fermer le modal
                await handleDeleteReport(); // supprimer et déclencher snackbar
              }}
            >
              {t("Delete")}
            </Button>
          </Stack>
        </Box>
      </Modal>
    </div>
  );
}

export default ReportEditor;
